import { classes } from "@/utils"

/**
 * Collapsible, plain-English explanation of exactly what the QuickBooks sync does, in the order
 * the sync job runs. Shown on the Accounting settings page (next to the sync banner) and on the
 * cash application page. Content mirrors `server/src/integration/quickbooks/job.rs` — keep the
 * two in sync when the job changes.
 */
export function QuickBooksSyncExplainer({ className }: { className?: string }) {
  return (
    <details class={classes("group rounded-lg border border-gray-200 bg-gray-50", className)}>
      <summary class="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900">
        How does the QuickBooks sync work?
        <span class="ml-2 text-xs font-normal text-gray-400 group-open:hidden">(click to expand)</span>
      </summary>
      <div class="space-y-3 border-t border-gray-200 px-4 py-4 text-sm text-gray-600">
        <p>
          The sync <span class="font-medium">reads</span> from QuickBooks — it never creates or changes anything in
          QuickBooks on its own. Records are only written to QuickBooks when you explicitly publish a credit memo or
          confirm a cash application. Each sync runs these steps in order:
        </p>
        <ol class="list-decimal space-y-2 pl-5">
          <li>
            <span class="font-medium text-gray-700">Chart of Accounts → Ledger Accounts.</span> Every QuickBooks
            account is matched to a MarginWiz ledger account (by QuickBooks id, then account number, then name) and
            its name, number, and type are refreshed. Accounts that don&apos;t exist here yet are added, hidden from
            deduction matching by default.
          </li>
          <li>
            <span class="font-medium text-gray-700">Items (Products &amp; Services).</span> All active QuickBooks
            items are fetched. When an item points at an account whose ledger account has no{" "}
            <span class="font-medium">Item</span> mapping yet, the mapping is filled in automatically
            (existing mappings are never overwritten). This mapping matters because QuickBooks credit memo lines can
            only reference items — never accounts directly — so a GL account without a mapped item
            can&apos;t be published until an item exists (MarginWiz offers to create one for you when that happens).
          </li>
          <li>
            <span class="font-medium text-gray-700">Invoices.</span> All QuickBooks invoices are synced, including
            open balances. These are what deductions get matched against and what payments get applied to during cash
            application.
          </li>
          <li>
            <span class="font-medium text-gray-700">Payments (checks).</span> All QuickBooks customer payments are
            synced and auto-matched to MarginWiz checks by check number and amount, so checks that were already
            applied in QuickBooks show as applied here.
          </li>
          <li>
            <span class="font-medium text-gray-700">Customers.</span> The QuickBooks customer list is synced for the
            customer picker on cash application.
          </li>
          <li>
            <span class="font-medium text-gray-700">Credit memos.</span> Existing QuickBooks credit memos are synced
            and auto-matched to checks (by check number in the memo or document number), so deduction write-offs done
            directly in QuickBooks are recognized.
          </li>
        </ol>
        <p>
          A full sync can take several minutes for organizations with thousands of invoices — QuickBooks serves large
          lists slowly. The sync runs when you press <span class="font-medium">Sync</span> and on your
          organization&apos;s configured schedule.
        </p>
      </div>
    </details>
  )
}
